import { Clock } from 'lucide-react';
import { Kbd } from '@/components/Kbd';
import { useTimerActions } from '@/components/timer/TimerActionsProvider';
import { useTimer } from '@/hooks/use-timer';
import { cn } from '@/lib/cn';
import { SHORTCUT_HINTS } from '@/lib/keyboard-shortcuts';

const START_HINT = SHORTCUT_HINTS['start-timer'];
const DETAILS_HINT = SHORTCUT_HINTS['start-with-details'];
/** Sprint 13: the title of the disabled start controls while on shift. */
export const ON_SHIFT_TITLE = 'You are on shift';

interface TimerStartButtonsProps {
  variant: 'inline' | 'full';
  onStartInstant: () => void;
  onStartWithDetails: () => void;
}

const SEGMENT_FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

/**
 * The original two-segment start control: "Start timer" (instant) | "Start with details…".
 * Renders nothing while the timer is loading or a timer exists.
 */
export function TimerStartButtons({ variant, onStartInstant, onStartWithDetails }: TimerStartButtonsProps): JSX.Element | null {
  const timer = useTimer();
  const actions = useTimerActions();
  if (timer.isLoading || timer.data?.timer) return null;
  // Sprint 13 (§8): disabled, not hidden, while on shift; the API refuses a start anyway (ON_SHIFT).
  const onShift = actions.onShift;
  const disabled = actions.pending || onShift;

  const padding = variant === 'inline' ? 'py-2 sm:flex-none sm:py-1.5' : 'py-2.5';
  return (
    <div
      className={cn(
        variant === 'inline' ? 'flex w-full sm:inline-flex sm:w-auto' : 'flex w-full',
        'rounded-lg border border-border bg-card p-0.5',
      )}
    >
      <button
        type="button"
        onClick={onStartInstant}
        disabled={disabled}
        aria-keyshortcuts={START_HINT.aria}
        title={onShift ? ON_SHIFT_TITLE : `Start timer (${START_HINT.title})`}
        className={cn(
          'flex flex-1 items-center justify-center gap-2 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover active:scale-[0.98]',
          SEGMENT_FOCUS,
          padding,
        )}
      >
        <Clock className="h-4 w-4" aria-hidden="true" /> Start timer <Kbd>{START_HINT.label}</Kbd>
      </button>
      <button
        type="button"
        onClick={onStartWithDetails}
        disabled={disabled}
        aria-keyshortcuts={DETAILS_HINT.aria}
        title={onShift ? ON_SHIFT_TITLE : `Start with details (${DETAILS_HINT.title})`}
        className={cn('flex flex-1 items-center justify-center gap-2 rounded-md px-3 text-sm text-muted-foreground transition-colors hover:text-foreground', SEGMENT_FOCUS, padding)}
      >
        Start with details… <Kbd>{DETAILS_HINT.label}</Kbd>
      </button>
    </div>
  );
}
